/* tslint:disable */
/* eslint-disable */
import { HttpClient, HttpContext, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';

import { WorkCalculation } from '../models/work-calculation';
import { WorkSaveCalculationAsTemplateDto } from '../models/work-save-calculation-as-template-dto';

@Injectable({ providedIn: 'root' })
export class WorkCalculationsV2Service extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /** Path part for operation `calculationsV2ControllerFindOneById()` */
  static readonly CalculationsV2ControllerFindOneByIdPath = '/v2/calculations/{id}';

  /**
   * Required permissions: projects:read, projects:admin
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `calculationsV2ControllerFindOneById()` instead.
   *
   * This method doesn't expect any request body.
   */
  calculationsV2ControllerFindOneById$Response(
    params: {
      id: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<WorkCalculation>> {
    const rb = new RequestBuilder(this.rootUrl, WorkCalculationsV2Service.CalculationsV2ControllerFindOneByIdPath, 'get');
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<WorkCalculation>;
      })
    );
  }

  /**
   * Required permissions: projects:read, projects:admin
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `calculationsV2ControllerFindOneById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  calculationsV2ControllerFindOneById(
    params: {
      id: number;
    },
    context?: HttpContext
  ): Observable<WorkCalculation> {
    return this.calculationsV2ControllerFindOneById$Response(params, context).pipe(
      map((r: StrictHttpResponse<WorkCalculation>): WorkCalculation => r.body)
    );
  }

  /** Path part for operation `calculationsV2ControllerFindAllTemplates()` */
  static readonly CalculationsV2ControllerFindAllTemplatesPath = '/v2/calculations/allTemplates/{containertypeId}';

  /**
   * Required permissions: projects:read, projects:admin
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `calculationsV2ControllerFindAllTemplates()` instead.
   *
   * This method doesn't expect any request body.
   */
  calculationsV2ControllerFindAllTemplates$Response(
    params: {
      containertypeId: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<Array<WorkCalculation>>> {
    const rb = new RequestBuilder(this.rootUrl, WorkCalculationsV2Service.CalculationsV2ControllerFindAllTemplatesPath, 'get');
    if (params) {
      rb.path('containertypeId', params.containertypeId, {});
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<WorkCalculation>>;
      })
    );
  }

  /**
   * Required permissions: projects:read, projects:admin
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `calculationsV2ControllerFindAllTemplates$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  calculationsV2ControllerFindAllTemplates(
    params: {
      containertypeId: number;
    },
    context?: HttpContext
  ): Observable<Array<WorkCalculation>> {
    return this.calculationsV2ControllerFindAllTemplates$Response(params, context).pipe(
      map((r: StrictHttpResponse<Array<WorkCalculation>>): Array<WorkCalculation> => r.body)
    );
  }

  /** Path part for operation `calculationsV2ControllerSaveAsTemplate()` */
  static readonly CalculationsV2ControllerSaveAsTemplatePath = '/v2/calculations/saveAsTemplate';

  /**
   * Required permissions: projects:update, projects:admin
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `calculationsV2ControllerSaveAsTemplate()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  calculationsV2ControllerSaveAsTemplate$Response(
    params: {
      body: WorkSaveCalculationAsTemplateDto
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<void>> {
    const rb = new RequestBuilder(this.rootUrl, WorkCalculationsV2Service.CalculationsV2ControllerSaveAsTemplatePath, 'patch');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(
      rb.build({ responseType: 'text', accept: '*/*', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * Required permissions: projects:update, projects:admin
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `calculationsV2ControllerSaveAsTemplate$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  calculationsV2ControllerSaveAsTemplate(
    params: {
      body: WorkSaveCalculationAsTemplateDto
    },
    context?: HttpContext
  ): Observable<void> {
    return this.calculationsV2ControllerSaveAsTemplate$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>): void => r.body)
    );
  }

  /** Path part for operation `calculationsV2ControllerDisableTemplate()` */
  static readonly CalculationsV2ControllerDisableTemplatePath = '/v2/calculations/disableTemplate/{calculationId}';

  /**
   * Required permissions: projects:update, projects:admin
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `calculationsV2ControllerDisableTemplate()` instead.
   *
   * This method doesn't expect any request body.
   */
  calculationsV2ControllerDisableTemplate$Response(
    params: {
      calculationId: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<void>> {
    const rb = new RequestBuilder(this.rootUrl, WorkCalculationsV2Service.CalculationsV2ControllerDisableTemplatePath, 'patch');
    if (params) {
      rb.path('calculationId', params.calculationId, {});
    }

    return this.http.request(
      rb.build({ responseType: 'text', accept: '*/*', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * Required permissions: projects:update, projects:admin
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `calculationsV2ControllerDisableTemplate$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  calculationsV2ControllerDisableTemplate(
    params: {
      calculationId: number;
    },
    context?: HttpContext
  ): Observable<void> {
    return this.calculationsV2ControllerDisableTemplate$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>): void => r.body)
    );
  }

  /** Path part for operation `calculationsV2ControllerImportFromTemplate()` */
  static readonly CalculationsV2ControllerImportFromTemplatePath = '/v2/calculations/importFromTemplate/{calculationTemplateId}/{calculationToModifyId}';

  /**
   * Required permissions: projects:update, projects:admin
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `calculationsV2ControllerImportFromTemplate()` instead.
   *
   * This method doesn't expect any request body.
   */
  calculationsV2ControllerImportFromTemplate$Response(
    params: {
      calculationTemplateId: number;
      calculationToModifyId: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<boolean>> {
    const rb = new RequestBuilder(this.rootUrl, WorkCalculationsV2Service.CalculationsV2ControllerImportFromTemplatePath, 'patch');
    if (params) {
      rb.path('calculationTemplateId', params.calculationTemplateId, {});
      rb.path('calculationToModifyId', params.calculationToModifyId, {});
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: String((r as HttpResponse<any>).body) === 'true' }) as StrictHttpResponse<boolean>;
      })
    );
  }

  /**
   * Required permissions: projects:update, projects:admin
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `calculationsV2ControllerImportFromTemplate$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  calculationsV2ControllerImportFromTemplate(
    params: {
      calculationTemplateId: number;
      calculationToModifyId: number;
    },
    context?: HttpContext
  ): Observable<boolean> {
    return this.calculationsV2ControllerImportFromTemplate$Response(params, context).pipe(
      map((r: StrictHttpResponse<boolean>): boolean => r.body)
    );
  }

  /** Path part for operation `calculationsV2ControllerImportFromProject()` */
  static readonly CalculationsV2ControllerImportFromProjectPath = '/v2/calculations/importFromProject/{projectId}/{calculationToModifyId}';

  /**
   * Required permissions: projects:update, projects:admin
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `calculationsV2ControllerImportFromProject()` instead.
   *
   * This method doesn't expect any request body.
   */
  calculationsV2ControllerImportFromProject$Response(
    params: {
      projectId: number;
      calculationToModifyId: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<boolean>> {
    const rb = new RequestBuilder(this.rootUrl, WorkCalculationsV2Service.CalculationsV2ControllerImportFromProjectPath, 'patch');
    if (params) {
      rb.path('projectId', params.projectId, {});
      rb.path('calculationToModifyId', params.calculationToModifyId, {});
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: String((r as HttpResponse<any>).body) === 'true' }) as StrictHttpResponse<boolean>;
      })
    );
  }

  /**
   * Required permissions: projects:update, projects:admin
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `calculationsV2ControllerImportFromProject$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  calculationsV2ControllerImportFromProject(
    params: {
      projectId: number;
      calculationToModifyId: number;
    },
    context?: HttpContext
  ): Observable<boolean> {
    return this.calculationsV2ControllerImportFromProject$Response(params, context).pipe(
      map((r: StrictHttpResponse<boolean>): boolean => r.body)
    );
  }

}
